// React
import { memo, useRef, useCallback, useEffect } from 'react'

// Uploady
import {
  useAbortAll,
  useAbortItem,
  useItemStartListener,
  useItemProgressListener,
  useItemFinishListener,
  useItemErrorListener,
  useItemAbortListener,
  useAllAbortListener,
  FILE_STATES,
} from '@rpldy/chunked-uploady'
import { useRetry } from '@rpldy/retry-hooks'
import { asUploadButton, ButtonProps } from '@rpldy/upload-button'

// Styles
import styles from 'components/base/BaseUploader/base-uploader.module.scss'

// Interfaces
import { IContentProps } from './interfaces'

// Utils
import { messageUtils_error } from 'utils/common/message.utils'

// i18n
import { useTranslation } from 'react-i18next'

// Components
import { BaseImage, BaseImagePreview } from 'components'
import { PreviewImages, PreviewImagesLabel } from 'components/base/BaseUploader/BaseUploader/components'

// Uploady
import UploadPreview from '@rpldy/upload-preview'

// Constant
import { FILE_SIZE_3_MB, FORMAT_BYTES } from 'constant/file.constant'

const Content = memo(
  ({
    isForceAbort,
    files,
    fileAcceptFormats,
    isUsingPreview,
    isError,
    uploadedFilesLength,
    checkUploadedFilesLength,
    maximumFileUpload,
    onChange,
  }: IContentProps) => {
    // Hook
    const { t } = useTranslation()
    const abortAll = useAbortAll()
    const retry = useRetry()
    const abortItem = useAbortItem()
    const uploadyButtonRef = useRef<any>(null)

    // Handle when user want to abort all request upload
    useEffect(() => {
      if (isForceAbort) abortAll()

      // eslint-disable-next-line
    }, [isForceAbort])

    /**
     * @description Watch any change when user click upload content
     *
     * @return {void} void
     */
    const onClick = (): void => {
      if (uploadyButtonRef.current) uploadyButtonRef.current!.click()
    }

    /**
     * @description Uploady Button
     *
     * @return {any}
     */
    const UploadyButton = asUploadButton((uploadyButtonProps: ButtonProps) => {
      return (
        <div {...uploadyButtonProps} className="hidden" ref={uploadyButtonRef}>
          Upload
        </div>
      )
    })

    /**
     * @description Watch any change when user try to retry the uploaded content
     *
     * @param {string} id
     *
     * @return {void} void
     */
    const onRetry = useCallback(
      id => {
        const isUploading = files.some(file => file.state === FILE_STATES.UPLOADING)

        // Check for files upload
        if (isUploading) {
          messageUtils_error(t('app.validations.files.waitingQue'))

          return
        }

        retry(id)
      },
      [retry, files, t],
    )

    /**
     * @description Watch any change when user try to abort the uploaded content
     *
     * @param {string} id
     *
     * @return {void} void
     */
    const onAbort = useCallback(
      id => {
        abortItem(id)
      },
      [abortItem],
    )

    /**
     * @description Get preview props for component
     *
     * @return {PreviewComponentProps} PreviewComponentProps
     */
    const getPreviewProps = useCallback(item => ({ id: item.id, name: item.file.name }), [])

    // Watch any change when user start uploading a file
    useItemStartListener(item => {
      // Check if user want to check length uploaded file according to uploaded files
      if (checkUploadedFilesLength) {
        if (uploadedFilesLength! === maximumFileUpload!) {
          messageUtils_error(t('app.validations.fileReachMaximumUpload'))

          return false
        }
      }

      if (item.file.size >= FILE_SIZE_3_MB) {
        messageUtils_error(`${item.file.name} ${t('app.validations.fileTooBig')} ${FORMAT_BYTES(FILE_SIZE_3_MB)}`)
        return false
      }

      // Check the format
      if (!fileAcceptFormats?.includes(item.file.type)) {
        messageUtils_error(`${item.file.name} ${t('app.validations.notValid')}`)
        return false
      }

      // Check user concurrent with 10 file
      if (files.length > 7 && !item.recycled) {
        messageUtils_error(t('app.validations.fileReachMaximumUpload'))
        return false
      }

      onChange(FILE_STATES.ADDED, item)
    })

    // Watch any change when file start to be uploaded to server
    useItemProgressListener(item => {
      onChange(FILE_STATES.UPLOADING, item)
    })

    // Watch any change when file finish get uploaded
    useItemFinishListener(item => {
      onChange(FILE_STATES.FINISHED, item)
    })

    // Watch any change when file that uploaded error
    useItemErrorListener(item => {
      onChange(FILE_STATES.ERROR, item)
    })

    // Watch any change when file got aborted
    useItemAbortListener(item => {
      onChange(FILE_STATES.ABORTED, item)
    })

    // Watch any change when all file got aborted
    useAllAbortListener(() => {
      onChange(FILE_STATES.ABORTED, undefined)
    })

    return (
      <div className={styles.container}>
        <div className={`${styles.content} ${isError ? styles['content-error'] : ''}`} onClick={onClick}>
          {/* Render when files empty */}
          {files.length === 0 && (
            <div className={styles['content-empty']}>
              <BaseImage isIcon isSvg icon="back-office/icon-upload" />
              <p className="font-14 text-grey font-weight-400 mt-6">{t('app.fileLabel.selectImages')}</p>
            </div>
          )}

          {/* Render when user is using preview */}
          {isUsingPreview && (
            <div className={`${files.length === 0 ? 'hidden' : ''}`}>
              <BaseImagePreview>
                <div className={`grid grid-cols-4 gap-8 ${styles.image}`} id="content-uploady-container">
                  <UploadPreview
                    previewComponentProps={getPreviewProps}
                    PreviewComponent={props => (
                      <PreviewImages {...props} files={files} onRetry={onRetry} onChange={onChange} onAbort={onAbort} />
                    )}
                    rememberPreviousBatches
                  />
                </div>
              </BaseImagePreview>
            </div>
          )}

          {/* Render when user not using preview */}
          {!isUsingPreview && <PreviewImagesLabel files={files} onChange={onChange} onRetry={onRetry} onAbort={onAbort} />}
        </div>

        <UploadyButton />
      </div>
    )
  },
)

export { Content }
